// Cohort risk table — the ranking that drives the Cohort Risk section.
//
// FM-6: each row carries an info button opening that cohort's breakdown, so the
//       numbers can be explained in place instead of only in the methodology.
// FM-7: the team name itself is the drill-down into individual-level detail,
//       shown as a link only when the viewer is allowed it (see FM-10).

import React from 'react';
import { Info, Users } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { CohortFatigueSummary, DerivedMetricKey } from '../data/types';
import { tierForMetric } from '../data/methodology';
import { Sparkline, TierBadge, tierTextClass } from './primitives';

const METRIC_COLUMNS: {
  key: DerivedMetricKey;
  head: string;
  value: (s: CohortFatigueSummary) => number;
  format: (v: number) => string;
}[] = [
  { key: 'fed', head: 'FED /1k', value: (s) => s.fedPer1000, format: (v) => String(v) },
  { key: 'lrm', head: 'LRM', value: (s) => s.lrmPct, format: (v) => `${v}%` },
  { key: 'cdi', head: 'CDI', value: (s) => s.cdiScore, format: (v) => String(v) },
  { key: 'pdc', head: 'PDC', value: (s) => s.pdcPct, format: (v) => `${v}%` },
];

export const CohortRiskTable: React.FC<{
  summaries: CohortFatigueSummary[];
  /** FM-6 — open the cohort breakdown panel. */
  onOpenCohort: (cohortId: string) => void;
  /** FM-7 — drill into the people in this cohort. Absent when not permitted. */
  onDrillToIndividuals?: (cohortId: string) => void;
}> = ({ summaries, onOpenCohort, onDrillToIndividuals }) => (
  <div className='overflow-x-auto'>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Team</TableHead>
          <TableHead>Site</TableHead>
          <TableHead className='text-right'>People</TableHead>
          {METRIC_COLUMNS.map((column) => (
            <TableHead key={column.key} className='text-right'>
              {column.head}
            </TableHead>
          ))}
          <TableHead>Trend</TableHead>
          <TableHead>Tier</TableHead>
          <TableHead className='w-10'>
            <span className='sr-only'>Breakdown</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {summaries.map((summary) => {
          // A team with too few wearers, or none at all, has no figures to show.
          // Its cells read as dashes rather than zeros — zero fatigue exposure and
          // no measurement are opposite findings.
          const blank = summary.suppressed || summary.noData;
          return (
            <TableRow key={summary.cohort.id}>
              <TableCell className='font-medium'>
                {onDrillToIndividuals && !blank ? (
                  <button
                    type='button'
                    onClick={() => onDrillToIndividuals(summary.cohort.id)}
                    className='inline-flex items-center gap-1.5 rounded-sm underline decoration-dotted underline-offset-4 hover:text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
                  >
                    <Users className='size-3.5 text-muted-foreground' aria-hidden />
                    {summary.cohort.name}
                    <span className='sr-only'>— view individuals</span>
                  </button>
                ) : (
                  summary.cohort.name
                )}
                <span className='block text-xs font-normal text-muted-foreground'>
                  <span className='capitalize'>{summary.cohort.shiftType}</span> shift (inferred)
                  {blank && (summary.noData ? ' · no wearable data' : ' · too few wearers to report')}
                </span>
              </TableCell>
              <TableCell className='text-muted-foreground'>{summary.cohort.site}</TableCell>
              <TableCell className='text-right tabular-nums'>{summary.cohort.headcount}</TableCell>
              {METRIC_COLUMNS.map((column) => {
                const value = column.value(summary);
                return (
                  <TableCell
                    key={column.key}
                    className={cn(
                      'text-right font-medium tabular-nums',
                      blank ? 'text-muted-foreground' : tierTextClass[tierForMetric(column.key, value)],
                    )}
                  >
                    {blank ? '—' : column.format(value)}
                  </TableCell>
                );
              })}
              <TableCell>
                <Sparkline values={summary.sparkline} tier={summary.riskTier} />
              </TableCell>
              <TableCell>
                {blank ? <span className='text-muted-foreground'>—</span> : <TierBadge tier={summary.riskTier} />}
              </TableCell>
              <TableCell>
                <button
                  type='button'
                  onClick={() => onOpenCohort(summary.cohort.id)}
                  aria-label={`Breakdown for ${summary.cohort.name}`}
                  className='rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
                >
                  <Info className='size-4' aria-hidden />
                </button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);
